import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
// '/pure' does NOT inject js.stripe.com on import — only when loadStripe() is actually
// called (i.e. when Stripe keys are configured). Avoids a network hit/failure on every load.
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { api } from './api.js';
import LakeMap from './LakeMap.jsx';

const STAGES = ['offered', 'accepted', 'enroute', 'arrived', 'in_progress', 'completed'];
const STAGE_LABEL = {
  offered: 'Captain offered', accepted: 'Accepted', enroute: 'On the way',
  arrived: 'At pickup', in_progress: 'Aboard', completed: 'Dropped off',
};
const NAV = [['rider', 'Book a Ride'], ['captains', 'Captains'], ['console', 'Captain'], ['join', 'Become a Captain'], ['admin', 'Admin']];
const money = (n) => (n == null ? '—' : '$' + Number(n).toFixed(2));
const asPoint = (p) => ({ lat: p.lat, lng: p.lng, label: p.name, place_id: p.id });
const stars = (n) => '★★★★★'.slice(0, Math.round(n)) + '☆☆☆☆☆'.slice(0, 5 - Math.round(n));

const formatPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const LICENSE_TYPES = [
  { value: 'OUPV (6-pack)', prefix: '6PK' },
  { value: 'Master 25-ton', prefix: 'M25' },
  { value: 'Master 50-ton', prefix: 'M50' },
  { value: 'Master 100-ton', prefix: 'M100' },
];
const licPrefix = (t) => LICENSE_TYPES.find((x) => x.value === t)?.prefix || 'LIC';
const formatLicenseNo = (type, v) => `${licPrefix(type)}-${(v || '').replace(/\D/g, '').slice(0, 7)}`;

function fileToDataUrl(file, max = 1100, quality = 0.82) {
  return new Promise((resolve) => {
    const rd = new FileReader();
    rd.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(cv.toDataURL('image/jpeg', quality));
      };
      img.src = rd.result;
    };
    rd.readAsDataURL(file);
  });
}

const LAKES = ['Smith Mountain Lake', 'Lake Anna (soon)', 'Claytor Lake (soon)', 'Leesville Lake (soon)'];

const IS_SANDBOX = (import.meta.env.VITE_ENV || '') === 'sandbox';
function SandboxBar() {
  if (!IS_SANDBOX) return null;
  return (
    <div style={{ background: 'var(--accent)', color: '#fff', textAlign: 'center', fontWeight: 700, fontSize: 12.5, padding: '5px 10px', letterSpacing: '.02em' }}>
      🧪 SANDBOX — test data only. This is not the live DockDash.
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(() => { try { return JSON.parse(localStorage.getItem('ll_session') || 'null'); } catch { return null; } });
  const store = (s) => { setSession(s); if (s) localStorage.setItem('ll_session', JSON.stringify(s)); else localStorage.removeItem('ll_session'); };
  const [places, setPlaces] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [lake, setLake] = useState(LAKES[0]);
  const [profileId, setProfileId] = useState(null);
  const [riderId, setRiderId] = useState(null);

  const refreshCaptains = useCallback(() => api.captains().then(setCaptains), []);
  useEffect(() => { api.places().then(setPlaces); refreshCaptains(); const t = setInterval(refreshCaptains, 2500); return () => clearInterval(t); }, [refreshCaptains]);

  const NAV = !session ? []
    : session.kind === 'rider' ? [['rider', 'Book a Ride'], ['captains', 'Captains'], ['join', 'Become a Captain']]
      : session.kind === 'captain' ? [['console', 'My Console'], ['captains', 'Captains']]
        : [['admin', 'Dashboard'], ['captains', 'Captains'], ['join', 'Become a Captain']];
  const [role, setRole] = useState('rider');
  useEffect(() => { if (session) setRole(session.kind === 'admin' ? 'admin' : session.kind === 'captain' ? 'console' : 'rider'); }, [session?.kind]);

  if (!session) return (<><SandboxBar /><SignIn onSignIn={store} refreshCaptains={refreshCaptains} /></>);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SandboxBar />
      <header style={{ background: 'var(--grad-brand)', color: '#fff' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto' }} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div style={{ width: 40, height: 40, background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.25)' }} className="rounded-2xl grid place-items-center text-xl">⚓</div>
            <div className="flex items-center gap-2.5">
              <div className="ll-title" style={{ fontSize: 22, lineHeight: 1 }}>DockDash</div>
              <LakeSelector lake={lake} onChange={setLake} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <nav style={{ background: 'rgba(0,0,0,.16)' }} className="flex gap-1 rounded-xl p-1">
              {NAV.map(([k, label]) => (
                <button key={k} onClick={() => setRole(k)} className="px-3 py-1.5 rounded-lg text-sm transition"
                  style={role === k ? { background: '#fff', color: 'var(--brand-deep)', fontWeight: 700 } : { color: 'rgba(255,255,255,.85)', fontWeight: 600 }}>{label}</button>
              ))}
            </nav>
            <NotificationBell session={session} />
            <div className="flex items-center gap-1.5" style={{ fontSize: 12.5 }}>
              <span style={{ color: 'rgba(255,255,255,.9)', fontWeight: 600 }} className="hidden sm:inline">{session.name}</span>
              <button onClick={() => store(null)} style={{ color: 'rgba(255,255,255,.8)', textDecoration: 'underline' }}>Sign out</button>
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1240, margin: '0 auto', width: '100%' }} className="p-3 sm:p-4 flex-1">
        {role === 'rider' && <RiderView places={places} captains={captains} rider={session} onCaptainClick={setProfileId} />}
        {role === 'captains' && <CaptainsDirectory captains={captains} onOpen={setProfileId} />}
        {role === 'console' && <CaptainConsole captains={captains} places={places} onChange={refreshCaptains} onCaptainClick={setProfileId} lockedId={session.kind === 'captain' ? session.id : null} />}
        {role === 'join' && <BecomeCaptain onDone={refreshCaptains} />}
        {role === 'admin' && <AdminView onOpenCaptain={setProfileId} onOpenRider={setRiderId} />}
      </main>

      {profileId && <Modal onClose={() => setProfileId(null)}><CaptainProfile id={profileId} onBack={() => setProfileId(null)} backLabel="✕ Close" /></Modal>}
      {riderId && <Modal onClose={() => setRiderId(null)}><RiderProfile id={riderId} onClose={() => setRiderId(null)} /></Modal>}
    </div>
  );
}

function SignIn({ onSignIn, refreshCaptains }) {
  const [mode, setMode] = useState('signin');
  const [ident, setIdent] = useState('');
  const [sent, setSent] = useState(false);
  const [channel, setChannel] = useState('email');
  const [devCode, setDevCode] = useState('');
  const [code, setCode] = useState('');
  const [needsName, setNeedsName] = useState(false);
  const [name, setName] = useState('');
  const [err, setErr] = useState('');

  const send = async () => { setErr(''); const r = await api.requestCode(ident); if (r.error) { setErr(r.error); return; } setSent(true); setChannel(r.channel); setDevCode(r.devCode || ''); };
  const verify = async () => {
    setErr('');
    const r = await api.verifyCode(ident, code, name);
    if (r.error) { setErr(r.error); return; }
    if (r.needsName) { setNeedsName(true); return; }
    if (r.user) onSignIn(r.user);
  };

  if (mode === 'join') return (
    <div style={{ minHeight: '100vh' }} className="p-4">
      <div style={{ maxWidth: 820, margin: '0 auto' }}><button onClick={() => setMode('signin')} className="ll-btn ll-btn-ghost mb-3" style={{ padding: '6px 12px' }}>← Back to sign in</button></div>
      <BecomeCaptain onDone={refreshCaptains} />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }} className="p-4">
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div className="text-center mb-4">
          <div style={{ width: 56, height: 56, background: 'var(--grad-brand)', color: '#fff', margin: '0 auto' }} className="rounded-2xl grid place-items-center text-3xl">⚓</div>
          <h1 className="ll-title mt-2" style={{ fontSize: 28 }}>DockDash</h1>
          <div style={{ color: 'var(--ink-soft)' }}>Smith Mountain Lake water shuttle — sign in to ride or drive.</div>
        </div>
        <Card>
          {!sent ? (<>
            <Field label="Email or phone"><input className="ll-input" value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="you@example.com or (540) 555-0100" onKeyDown={(e) => e.key === 'Enter' && send()} /></Field>
            <button className="ll-btn ll-btn-primary w-full mt-3" disabled={!ident.trim()} onClick={send}>Send code</button>
          </>) : (<>
            <div style={{ color: 'var(--ink-soft)', fontSize: 13 }} className="mb-2">We sent a code to {ident} {channel === 'sms' ? '(text)' : '(email)'}.</div>
            <Field label="6-digit code"><input className="ll-input tabular" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" onKeyDown={(e) => e.key === 'Enter' && verify()} /></Field>
            {needsName && <div className="mt-2"><Field label="Your name (new account)"><input className="ll-input" value={name} onChange={(e) => setName(e.target.value)} /></Field></div>}
            {devCode && <div style={{ color: 'var(--ink-faint)', fontSize: 12 }} className="mt-1">demo code: {devCode}</div>}
            <button className="ll-btn ll-btn-primary w-full mt-3" disabled={!code.trim() || (needsName && !name.trim())} onClick={verify}>{needsName ? 'Create account & continue' : 'Sign in'}</button>
            <button className="ll-btn ll-btn-ghost w-full mt-2" onClick={() => { setSent(false); setCode(''); setNeedsName(false); }}>Use a different email/phone</button>
          </>)}
          {err && <div style={{ color: '#c0392b', fontSize: 13 }} className="mt-2">{err}</div>}
        </Card>
        <div className="text-center mt-3">
          <button onClick={() => setMode('join')} style={{ color: 'var(--brand)', fontWeight: 600, fontSize: 14 }}>Want to captain? Become a Captain →</button>
        </div>
      </div>
    </div>
  );
}

function NotificationBell({ session }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => api.feed(session.kind, session.id).then(setItems);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [session.id]);
  const unread = items.filter((i) => !i.read).length;
  const toggle = async () => { const n = !open; setOpen(n); if (n && unread) { await api.feedRead(session.kind, session.id); load(); } };
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={toggle} style={{ position: 'relative', fontSize: 20, lineHeight: 1, padding: '4px 6px' }}>🔔
        {unread > 0 && <span style={{ position: 'absolute', top: -2, right: -2, background: '#e8622a', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 700, minWidth: 16, height: 16, display: 'grid', placeItems: 'center', padding: '0 3px' }}>{unread}</span>}
      </button>
      {open && (
        <div className="ll-card" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, width: 320, maxHeight: 380, overflowY: 'auto', zIndex: 60, padding: 8 }}>
          <div className="ll-label" style={{ padding: '4px 8px', color: 'var(--ink-soft)' }}>Notifications</div>
          {items.length === 0 ? <div style={{ color: 'var(--ink-faint)', padding: '8px' }} className="text-sm">Nothing yet.</div> :
            items.map((n) => (
              <div key={n.id} style={{ padding: '8px', borderTop: '1px solid var(--line)' }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--ink)' }}>{n.subject}</div>
                <div style={{ color: 'var(--ink-soft)', fontSize: 12.5 }}>{n.body}</div>
                <div style={{ color: 'var(--ink-faint)', fontSize: 11 }}>{n.created_at ? new Date(n.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function Modal({ onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,23,29,.55)', backdropFilter: 'blur(3px)', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '5vh 12px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 720 }}>{children}</div>
    </div>
  );
}

function LakeSelector({ lake, onChange }) {
  return (
    <div style={{ position: 'relative' }}>
      <select value={lake} onChange={(e) => { if (!e.target.value.includes('soon')) onChange(e.target.value); }}
        style={{ appearance: 'none', background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.28)', color: '#fff', fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 12.5, borderRadius: 999, padding: '4px 26px 4px 12px', cursor: 'pointer' }}>
        {LAKES.map((l) => <option key={l} value={l} disabled={l.includes('soon')} style={{ color: '#0e2733' }}>📍 {l}</option>)}
      </select>
      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 10, color: 'rgba(255,255,255,.8)' }}>▾</span>
    </div>
  );
}

function Card({ title, right, children, className = '', style }) {
  return (
    <section className={`ll-card ll-card-pad ${className}`} style={style}>
      {(title || right) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h2 className="ll-title" style={{ fontSize: 17 }}>{title}</h2>}
          {right}
        </div>
      )}
      {children}
    </section>
  );
}
function Chip({ children, tone }) {
  const s = tone === 'accent' ? { background: 'var(--accent-tint)', color: 'var(--accent-deep)' } : { background: 'var(--brand-tint)', color: 'var(--brand-deep)' };
  return <span className="ll-pill tabular" style={s}>{children}</span>;
}

/* ---------------- RIDER ---------------- */
function LocationInput({ glyph, color, placeholder, point, places, favorites = [], onFocusMode, onResolve }) {
  const [text, setText] = useState(point?.label || '');
  const [open, setOpen] = useState(false);
  const [addr, setAddr] = useState([]);
  const [busy, setBusy] = useState(false);
  const previousPointLabel = useRef(point?.label || '');
  useEffect(() => {
    const next = point?.label || '';
    if (next) setText(next);
    else if (previousPointLabel.current && text === previousPointLabel.current) setText('');
    previousPointLabel.current = next;
  }, [point?.label]);

  const q = text.trim().toLowerCase();
  const favs = (q ? favorites.filter((f) => f.label.toLowerCase().includes(q)) : favorites).slice(0, 4);
  const attractions = (q ? places.filter((p) => p.name.toLowerCase().includes(q)) : places).slice(0, 6);

  useEffect(() => {
    if (q.length < 3 || (point && text === point.label)) { setAddr([]); return; }
    const id = setTimeout(async () => { setBusy(true); const r = await api.geocodeSuggest(text.trim()); setBusy(false); setAddr(r.suggestions || []); }, 350);
    return () => clearTimeout(id);
  }, [text]);

  const pick = (pt) => { setOpen(false); setAddr([]); onResolve(pt); };
  const row = { display: 'block', width: '100%', textAlign: 'left', padding: '7px 8px', borderRadius: 8, fontSize: 13.5, color: 'var(--ink)' };
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
      <div className="flex items-center gap-2 rounded-xl px-2.5 py-1.5" style={{ border: '1px solid var(--line-strong)', background: 'var(--surface)' }}>
        <span style={{ background: color, width: 22, height: 22, borderRadius: 7, color: '#fff', fontWeight: 700, fontSize: 12, display: 'grid', placeItems: 'center', flex: 'none' }}>{glyph}</span>
        <input value={text} placeholder={placeholder}
          onFocus={() => { onFocusMode(); setOpen(true); }}
          onChange={(e) => {
            const value = e.target.value;
            setText(value);
            setOpen(true);
            if (point && value !== point.label) onResolve(null);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
            if (e.key === 'Escape') e.currentTarget.blur();
          }}
          aria-invalid={text.trim() && !point ? 'true' : undefined}
          style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: 14, color: 'var(--ink)' }} />
        {busy && <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>…</span>}
        {point && !busy && <span onMouseDown={(e) => e.preventDefault()} onClick={() => { setText(''); onResolve(null); }} style={{ color: 'var(--ink-faint)', cursor: 'pointer' }}>✕</span>}
      </div>
      {open && (favs.length > 0 || attractions.length > 0 || addr.length > 0) && (
        <div className="ll-card" style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, marginTop: 4, maxHeight: 300, overflowY: 'auto', padding: 6 }}>
          {text.trim() && !point && <div style={{ padding: '5px 8px', color: 'var(--brand-deep)', fontSize: 12, fontWeight: 600 }}>Select a waterfront result below.</div>}
          {favs.length > 0 && <div className="ll-label" style={{ padding: '4px 8px' }}>Saved</div>}
          {favs.map((f) => <button key={f.id} onMouseDown={(e) => e.preventDefault()} onClick={() => pick({ lat: f.lat, lng: f.lng, label: f.label })} style={row} onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>★ {f.label}</button>)}
          {attractions.length > 0 && <div className="ll-label" style={{ padding: '4px 8px' }}>Attractions</div>}
          {attractions.map((p) => <button key={p.id} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(asPoint(p))} style={row} onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>📍 {p.name} <span style={{ color: 'var(--ink-faint)', fontSize: 11 }}>· {p.kind}</span></button>)}
          {addr.length > 0 && <div className="ll-label" style={{ padding: '4px 8px' }}>Lake addresses</div>}
          {addr.map((a, i) => <button key={i} onMouseDown={(e) => e.preventDefault()} onClick={() => pick({ ...a.point, label: a.label })} style={row} onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>🏠 {a.label}</button>)}
        </div>
      )}
      {text.trim() && !point && !open && <div style={{ color: 'var(--accent-deep)', fontSize: 11, marginTop: 3 }}>Choose a waterfront result or select a point on the map.</div>}
    </div>
  );
}

function RiderView({ places, captains, rider, onCaptainClick }) {
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [mode, setMode] = useState('pickup');
  const [tiers, setTiers] = useState([]);
  const [tierId, setTierId] = useState('');
  const [party, setParty] = useState(2);
  const [when, setWhen] = useState('now');
  const [schedAt, setSchedAt] = useState('');
  const [quote, setQuote] = useState(null);
  const [ride, setRide] = useState(null);
  const [warn, setWarn] = useState('');
  const [payCfg, setPayCfg] = useState({ mode: 'mock', publishableKey: null });
  const [favorites, setFavorites] = useState([]);
  const rideRef = useRef(null);
  const maxCap = Math.max(6, ...captains.filter((c) => c.online && c.max_passengers).map((c) => c.max_passengers));

  const loadFavs = useCallback(() => { if (rider?.id) api.favorites(rider.id).then(setFavorites); }, [rider?.id]);
  useEffect(() => { loadFavs(); }, [loadFavs]);
  useEffect(() => { api.paymentMode().then(setPayCfg); }, []);
  useEffect(() => { if (ride?.id) rideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [ride?.id]);
  useEffect(() => { api.tiers().then((t) => { const a = t.filter((x) => x.active); setTiers(a); if (a[0]) setTierId(a[0].id); }); }, []);
  const curTier = tiers.find((t) => t.id === tierId);

  const setPoint = useCallback((pt) => { if (mode === 'pickup') { setPickup(pt); setMode('dropoff'); } else setDropoff(pt); }, [mode]);
  const onPlaceClick = useCallback((p) => { setWarn(''); setPoint(asPoint(p)); }, [setPoint]);
  const onMapClick = useCallback(async (ll) => {
    const v = await api.validatePoint(ll);
    if (!v.ok) { setWarn('That spot is off the lake — tap the water or a marina.'); return; }
    setWarn('');
    setPoint({ lat: v.point.lat, lng: v.point.lng, label: `Dropped pin (${v.point.lat.toFixed(3)}, ${v.point.lng.toFixed(3)})` });
  }, [setPoint]);

  const resolveA = (pt, w) => { if (w) { setWarn(w); return; } setWarn(''); setPickup(pt); };
  const resolveB = (pt, w) => { if (w) { setWarn(w); return; } setWarn(''); setDropoff(pt); };

  useEffect(() => {
    if (pickup && dropoff) api.quote({ pickup, dropoff, tier_id: tierId }).then(setQuote); else setQuote(null);
  }, [pickup, dropoff, tierId]);
  useEffect(() => {
    if (!ride || ['completed', 'cancelled', 'scheduled'].includes(ride.status)) return;
    const t = setInterval(() => api.ride(ride.id).then(setRide), 2000);
    return () => clearInterval(t);
  }, [ride]);

  const busy = ride && !['completed', 'cancelled'].includes(ride.status);
  const request = async () => {
    const scheduled_for = when === 'later' && schedAt ? new Date(schedAt).toISOString() : null;
    setRide(await api.requestRide({ rider_id: rider.id, pickup, dropoff, party_size: party, tier_id: tierId, scheduled_for }));
  };
  const reset = () => { setPickup(null); setDropoff(null); setMode('pickup'); setQuote(null); setRide(null); setWarn(''); };

  return (
    <div className="space-y-3">
      <div className="ll-card" style={{ padding: 12 }}>
        {/* line 1: A + B locations */}
        <div className="flex flex-col sm:flex-row gap-2">
          <LocationInput glyph="A" color="var(--good)" placeholder="Pickup — marina or waterfront address" point={pickup} places={places} favorites={favorites} onFocusMode={() => setMode('pickup')} onResolve={resolveA} />
          <LocationInput glyph="B" color="var(--accent)" placeholder="Dropoff — marina or waterfront address" point={dropoff} places={places} favorites={favorites} onFocusMode={() => setMode('dropoff')} onResolve={resolveB} />
        </div>
        {(pickup || dropoff || favorites.length > 0) && (
          <div className="flex items-center gap-2 flex-wrap mt-2" style={{ fontSize: 12 }}>
            {pickup && !favorites.some((f) => f.label === pickup.label) && <button onClick={async () => { await api.addFavorite(rider.id, pickup); loadFavs(); }} className="ll-pill" style={{ background: 'var(--surface-2)', color: 'var(--brand-deep)', cursor: 'pointer' }}>☆ Save pickup</button>}
            {dropoff && !favorites.some((f) => f.label === dropoff.label) && <button onClick={async () => { await api.addFavorite(rider.id, dropoff); loadFavs(); }} className="ll-pill" style={{ background: 'var(--surface-2)', color: 'var(--brand-deep)', cursor: 'pointer' }}>☆ Save dropoff</button>}
            {favorites.length > 0 && <span style={{ color: 'var(--ink-faint)' }}>Saved:</span>}
            {favorites.map((f) => (
              <span key={f.id} className="ll-pill" style={{ background: 'var(--brand-tint)', color: 'var(--brand-deep)' }}>
                <button onClick={() => setPoint({ lat: f.lat, lng: f.lng, label: f.label })} style={{ cursor: 'pointer' }}>★ {f.label.length > 22 ? f.label.slice(0, 22) + '…' : f.label}</button>
                <button onClick={async () => { await api.removeFavorite(f.id); loadFavs(); }} style={{ marginLeft: 4, color: 'var(--ink-faint)', cursor: 'pointer' }}>✕</button>
              </span>
            ))}
          </div>
        )}
        {/* line 2: tier, party, when + fare + request */}
        <div className="flex flex-wrap items-end gap-2 mt-2">
          {tiers.length > 1 && (
            <MiniField label="Tier">
              <select className="ll-select" style={{ padding: '6px 8px', minWidth: 120 }} value={tierId} onChange={(e) => setTierId(e.target.value)}>
                {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {curTier?.blurb && <span style={{ color: 'var(--ink-faint)', fontSize: 10.5, maxWidth: 170, lineHeight: 1.25 }} className="mt-0.5 block">{curTier.blurb}</span>}
            </MiniField>
          )}
          <MiniField label={maxCap > 6 ? `Party · up to ${maxCap}` : 'Party'}><input type="number" min="1" max={maxCap} className="ll-select tabular" style={{ padding: '6px 8px', width: 64 }} value={party} onChange={(e) => setParty(Math.min(maxCap, +e.target.value))} /></MiniField>
          <MiniField label="When">
            <div className="flex gap-1">
              <select className="ll-select" style={{ padding: '6px 8px' }} value={when} onChange={(e) => setWhen(e.target.value)}><option value="now">Now</option><option value="later">Schedule</option></select>
              {when === 'later' && <input type="datetime-local" className="ll-select" style={{ padding: '6px 8px' }} value={schedAt} onChange={(e) => setSchedAt(e.target.value)} />}
            </div>
          </MiniField>
          <div className="ml-auto flex items-end gap-4">
            {quote && (
              <div className="flex gap-3 text-right">
                <div><div className="ll-title tabular" style={{ fontSize: 16, color: 'var(--ink)', lineHeight: 1 }}>{quote.est_pickup_min ?? '—'}<span style={{ fontSize: 10 }}> min</span></div><div style={{ color: 'var(--ink-faint)', fontSize: 10 }}>pickup ETA</div></div>
                <div><div className="ll-title tabular" style={{ fontSize: 16, color: 'var(--ink)', lineHeight: 1 }}>{quote.trip_min}<span style={{ fontSize: 10 }}> min</span></div><div style={{ color: 'var(--ink-faint)', fontSize: 10 }}>on water</div></div>
              </div>
            )}
            <div className="text-right">
              {quote ? (<>
                <div className="ll-title tabular" style={{ fontSize: 24, color: 'var(--brand-deep)', lineHeight: 1 }}>{money(quote.fare)}</div>
                <div style={{ color: 'var(--ink-soft)', fontSize: 11 }} className="tabular">{quote.distance_nm} nm by water{quote.night ? ' · night' : ''}</div>
              </>) : <div style={{ color: 'var(--ink-faint)', fontSize: 12 }}>Set A &amp; B</div>}
            </div>
            <button className="ll-btn ll-btn-primary" disabled={!pickup || !dropoff || !rider || busy || (when === 'later' && !schedAt)} onClick={request}>{when === 'later' ? 'Schedule' : 'Request'}</button>
          </div>
        </div>
        {warn && <div style={{ background: 'var(--accent-tint)', color: 'var(--accent-deep)' }} className="rounded-lg px-3 py-1.5 text-sm mt-2">{warn}</div>}
      </div>

      <div className="ll-card !p-0 overflow-hidden">
        <div className="px-4 pt-2.5 pb-2 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="ll-label">Setting</span>
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }} className="flex rounded-lg p-0.5">
              {['pickup', 'dropoff'].map((m) => (
                <button key={m} onClick={() => setMode(m)} className="px-2.5 py-1 rounded-md text-xs transition"
                  style={mode === m ? { background: m === 'pickup' ? 'var(--good)' : 'var(--accent)', color: '#fff', fontWeight: 700 } : { color: 'var(--ink-soft)', fontWeight: 600 }}>{m === 'pickup' ? 'A · pickup' : 'B · dropoff'}</button>
              ))}
            </div>
            <span style={{ color: 'var(--ink-faint)', fontSize: 12 }} className="hidden sm:inline">Tap the water or a marina — inland is blocked</span>
          </div>
          <span style={{ color: 'var(--ink-faint)', fontSize: 12 }}>{captains.filter((c) => c.online).length} captains online</span>
        </div>
        <LakeMap places={places} captains={captains} pickup={pickup} dropoff={dropoff} route={quote?.path}
          activeCaptainId={ride?.captain_id} onMapClick={onMapClick} onPlaceClick={onPlaceClick} onCaptainClick={onCaptainClick} height="calc(100vh - 300px)" />
      </div>

      <div ref={rideRef} style={{ scrollMarginTop: 12 }}>
        {ride && <RideStatus ride={ride} payCfg={payCfg}
          onPay={async () => setRide(await api.pay(ride.id))}
          onStripeConfirmed={async () => setRide(await api.paymentConfirmed(ride.id))}
          onTip={async (amt) => setRide(await api.tip(ride.id, amt))}
          onCancel={async () => setRide(await api.cancel(ride.id))} onReset={reset} />}
      </div>
      {rider && <MyRides riderId={rider.id} />}
    </div>
  );
}

function MyRides({ riderId }) {
  const [open, setOpen] = useState(false);
  const [rides, setRides] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => { if (open) api.rides().then((r) => setRides(r.filter((x) => x.rider_id === riderId))); }, [open, riderId]);
  const term = q.trim().toLowerCase();
  const list = rides.filter((r) => !term || `${r.pickup_label} ${r.dropoff_label} ${r.business_name || ''} ${r.tier_name || ''}`.toLowerCase().includes(term));
  return (
    <Card title={`My rides${rides.length ? ` · ${rides.length}` : ''}`} right={<button onClick={() => setOpen((o) => !o)} className="ll-btn ll-btn-ghost" style={{ padding: '6px 12px' }}>{open ? 'Hide' : 'Show'}</button>}>
      {open && (<>
        <input className="ll-input mb-3" placeholder="🔍 Search past rides by place, captain, tier…" value={q} onChange={(e) => setQ(e.target.value)} />
        {list.length === 0 ? <div style={{ color: 'var(--ink-faint)' }} className="text-sm">No rides found.</div> : (
          <div className="space-y-1.5">
            {list.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-sm" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 6 }}>
                <div><div style={{ fontWeight: 600 }}>{r.pickup_label} → {r.dropoff_label}</div><div style={{ color: 'var(--ink-soft)', fontSize: 12 }}>{r.business_name || '—'} · {r.tier_name || 'Standard'} · {r.requested_at ? new Date(r.requested_at).toLocaleDateString() : ''}</div></div>
                <div className="text-right"><div className="tabular" style={{ fontWeight: 600 }}>{money(r.fare_final ?? r.fare_estimate)}</div><span className="ll-pill" style={{ background: 'var(--surface-2)', color: 'var(--ink-soft)', fontSize: 10 }}>{r.status}</span></div>
              </div>
            ))}
          </div>
        )}
      </>)}
    </Card>
  );
}
function MiniField({ label, children }) {
  return <div className="flex flex-col"><span className="ll-label" style={{ fontSize: 10, marginBottom: 2 }}>{label}</span>{children}</div>;
}

function RideStatus({ ride, payCfg, onPay, onStripeConfirmed, onTip, onCancel, onReset }) {
  const [rated, setRated] = useState(false);
  const idx = STAGES.indexOf(ride.status);
  const done = ride.status === 'completed', cancelled = ride.status === 'cancelled', scheduled = ride.status === 'scheduled';
  const needsPay = ride.payment_status === 'requires_payment', authorized = ride.payment_status === 'authorized';
  const useStripeCard = needsPay && payCfg?.mode === 'stripe_test' && payCfg.publishableKey && ride.payment_client_secret;
  return (
    <Card title={scheduled ? 'Scheduled ride' : 'Your ride'} style={{ maxWidth: 560, margin: '0 auto' }}>
      {!ride.captain_id ? <div style={{ color: 'var(--accent-deep)' }} className="text-sm">No captain online right now — searching…</div> : (<>
        <div className="flex items-center justify-between mb-3">
          <div><div style={{ fontWeight: 700 }}>{ride.business_name}</div><div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>{ride.vessel_name} · {ride.pickup_label} → {ride.dropoff_label}</div></div>
          {scheduled ? <Chip tone="accent">{new Date(ride.scheduled_for).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</Chip>
            : !done && !cancelled && <div className="text-right"><div className="ll-title tabular" style={{ fontSize: 22, color: 'var(--brand-deep)' }}>{ride.pickup_eta_min}<span style={{ fontSize: 12 }}> min</span></div><div style={{ color: 'var(--ink-faint)', fontSize: 11 }}>ETA to pickup</div></div>}
        </div>
        {cancelled ? <div style={{ color: '#c0392b', fontWeight: 600 }} className="text-sm mb-3">Ride cancelled.</div> : (
          <div className="mb-3">
            {useStripeCard
              ? <StripePay pk={payCfg.publishableKey} clientSecret={ride.payment_client_secret} amount={ride.fare_estimate} onConfirmed={onStripeConfirmed} />
              : needsPay && <button onClick={onPay} className="ll-btn ll-btn-accent w-full">💳 Confirm &amp; pay {money(ride.fare_estimate)} <span style={{ opacity: .8, fontWeight: 400, fontSize: 12 }}>test 4242</span></button>}
            {authorized && !scheduled && <Banner tone="good">✓ Payment authorized — charged on drop-off</Banner>}
            {authorized && scheduled && <Banner tone="good">✓ Reserved &amp; authorized — captain meets you at the scheduled time</Banner>}
            {done && ride.payment_status === 'paid' && <Banner tone="good">✓ Paid {money(ride.fare_final)} — thanks for riding!</Banner>}
          </div>
        )}
        {!cancelled && !scheduled && (
          <ol className="space-y-1.5">{STAGES.map((s, i) => { const st = done || i < idx ? 'done' : i === idx ? 'now' : 'todo'; return (
            <li key={s} className="flex items-center gap-2.5 text-sm" style={{ color: st === 'todo' ? 'var(--ink-faint)' : 'var(--ink)' }}>
              <span style={{ width: 15, height: 15, borderRadius: '50%', flex: 'none', background: st === 'done' ? 'var(--good)' : st === 'now' ? 'var(--brand)' : 'var(--line-strong)', boxShadow: st === 'now' ? '0 0 0 3px color-mix(in srgb, var(--brand) 22%, transparent)' : 'none' }} />{STAGE_LABEL[s]}
            </li>); })}</ol>
        )}
        {done && !rated && (
          <div style={{ borderTop: '1px solid var(--line)' }} className="mt-3 pt-3">
            <div className="ll-label mb-1.5">Rate {ride.business_name}</div>
            <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={async () => { await api.addReview(ride.captain_id, { author: 'Rider', stars: n, comment: '' }); setRated(true); }} style={{ fontSize: 24, color: 'var(--accent)' }}>★</button>)}</div>
          </div>
        )}
        {rated && <div style={{ color: 'var(--good)' }} className="text-sm mt-2">Thanks for the rating!</div>}
        {done && (ride.tip > 0
          ? <div style={{ color: 'var(--good)' }} className="text-sm mt-3">💚 You tipped {money(ride.tip)} — 100% to {ride.business_name}.</div>
          : <div className="mt-3" style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <div className="ll-label mb-1.5">Add a tip? (goes entirely to your captain)</div>
              <div className="flex gap-2 flex-wrap">
                {[5, 10, 15, 20].map((n) => <button key={n} onClick={() => onTip(n)} className="ll-btn ll-btn-ghost" style={{ padding: '7px 14px' }}>${n}</button>)}
                <button onClick={() => { const v = +prompt('Tip amount ($)'); if (v > 0) onTip(v); }} className="ll-btn ll-btn-ghost" style={{ padding: '7px 14px' }}>Custom</button>
              </div>
            </div>)}
        <div className="mt-4 flex gap-2">
          {!done && !cancelled && <button onClick={onCancel} className="ll-btn ll-btn-ghost flex-1">Cancel</button>}
          {(done || cancelled) && <button onClick={onReset} className="ll-btn ll-btn-primary flex-1">Book another ride</button>}
        </div>
      </>)}
    </Card>
  );
}
function Banner({ tone, children }) {
  const s = tone === 'good' ? { background: 'var(--good-tint)', color: '#0a6b49' } : { background: 'var(--surface-2)', color: 'var(--ink)' };
  return <div style={s} className="rounded-xl px-3 py-2 text-sm">{children}</div>;
}

function StripePay({ pk, clientSecret, amount, onConfirmed }) {
  const stripePromise = useMemo(() => loadStripe(pk), [pk]);
  return <Elements stripe={stripePromise}><StripeForm clientSecret={clientSecret} amount={amount} onConfirmed={onConfirmed} /></Elements>;
}
function StripeForm({ clientSecret, amount, onConfirmed }) {
  const stripe = useStripe();
  const elements = useElements();
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true); setErr('');
    const { error } = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: elements.getElement(CardElement) } });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onConfirmed();
  };
  return (
    <div>
      <div style={{ border: '1px solid var(--line-strong)', borderRadius: 11, padding: '11px 12px', background: 'var(--surface-2)', marginBottom: 8 }}>
        <CardElement options={{ style: { base: { fontSize: '15px', color: '#0e2733', '::placeholder': { color: '#8aa1ac' } } } }} />
      </div>
      {err && <div style={{ color: '#c0392b', fontSize: 12.5 }} className="mb-2">{err}</div>}
      <button onClick={pay} disabled={busy || !stripe} className="ll-btn ll-btn-accent w-full">💳 Pay {money(amount)} <span style={{ opacity: .8, fontWeight: 400, fontSize: 12 }}>Stripe test · card 4242 4242 4242 4242</span></button>
    </div>
  );
}

/* ---------------- CAPTAINS DIRECTORY + PROFILE ---------------- */
const ALL_SERVICES = ['Shuttle', 'Tours', 'Fishing', 'Tubing', 'Wakeboarding', 'Rentals'];
const parseServices = (c) => { try { return JSON.parse(c.services || '[]'); } catch { return []; } };
function CaptainsDirectory({ captains, onOpen }) {
  const [q, setQ] = useState('');
  const [svc, setSvc] = useState('');
  let list = captains.filter((c) => c.status === 'approved');
  const term = q.trim().toLowerCase();
  if (term) list = list.filter((c) => (c.business_name + ' ' + c.name).toLowerCase().includes(term));
  if (svc) list = list.filter((c) => parseServices(c).includes(svc));
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="ll-title" style={{ fontSize: 20 }}>Our licensed captains</h2>
        <input className="ll-input" placeholder="🔍 Search captains…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 240 }} />
      </div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <button onClick={() => setSvc('')} className="ll-pill" style={{ background: svc === '' ? 'var(--brand)' : 'var(--surface-2)', color: svc === '' ? '#fff' : 'var(--ink-soft)', cursor: 'pointer' }}>All</button>
        {ALL_SERVICES.map((s) => <button key={s} onClick={() => setSvc(s === svc ? '' : s)} className="ll-pill" style={{ background: svc === s ? 'var(--brand)' : 'var(--surface-2)', color: svc === s ? '#fff' : 'var(--ink-soft)', cursor: 'pointer' }}>{s}</button>)}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((c) => (
          <button key={c.id} onClick={() => onOpen(c.id)} className="ll-card ll-card-pad text-left" style={{ display: 'block' }}>
            <div className="flex items-center justify-between"><div style={{ fontWeight: 700 }}>{c.business_name}</div>{c.online && <Chip>online</Chip>}</div>
            <div style={{ color: 'var(--accent)', fontSize: 14 }} className="tabular">{stars(c.rating)} <span style={{ color: 'var(--ink-soft)' }}>{c.rating}</span></div>
            <div style={{ color: 'var(--ink-soft)', fontSize: 12.5 }} className="mt-1">{c.uscg_license_type} · {c.vessel_name}</div>
            <div className="flex gap-1 flex-wrap mt-2">{parseServices(c).map((s) => <span key={s} className="ll-pill" style={{ background: 'var(--brand-tint)', color: 'var(--brand-deep)', fontSize: 10.5 }}>{s}</span>)}</div>
            <div style={{ color: 'var(--brand)', fontSize: 13, fontWeight: 600 }} className="mt-2">View profile →</div>
          </button>
        ))}
        {list.length === 0 && <div style={{ color: 'var(--ink-faint)' }} className="text-sm">No captains match.</div>}
      </div>
    </div>
  );
}
function CaptainProfile({ id, onBack, backLabel = '← All captains' }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.captain(id).then(setC); }, [id]);
  if (!c) return null;
  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <button onClick={onBack} className="ll-btn ll-btn-ghost mb-3" style={{ padding: '6px 12px' }}>{backLabel}</button>
      <Card>
        <div className="flex items-start gap-3 flex-wrap">
          {c.photo_url && <img src={c.photo_url} alt="" style={{ width: 72, height: 72, borderRadius: 14, objectFit: 'cover', flex: 'none' }} />}
          <div className="flex-1" style={{ minWidth: 200 }}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <h2 className="ll-title" style={{ fontSize: 22 }}>{c.business_name}</h2>
                <div style={{ color: 'var(--ink-soft)' }}>{c.name} · {c.uscg_license_type}{c.home_marina ? ` · ${c.home_marina}` : ''}</div>
                <div style={{ color: 'var(--accent)', fontSize: 16 }} className="tabular mt-1">{stars(c.rating)} <span style={{ color: 'var(--ink-soft)' }}>{c.rating} · {c.rides_completed} rides</span></div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {c.online ? <Chip>online now</Chip> : <span className="ll-pill" style={{ background: 'var(--surface-2)', color: 'var(--ink-faint)' }}>offline</span>}
                {c.website && <a href={c.website} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)', fontSize: 13, fontWeight: 600 }}>Website ↗</a>}
              </div>
            </div>
          </div>
        </div>
        {c.bio && <p style={{ color: 'var(--ink)', lineHeight: 1.6 }} className="mt-3">{c.bio}</p>}
        {c.services?.length > 0 && (
          <div className="mt-3">
            <span className="ll-label">Services</span>
            <div className="flex gap-1.5 mt-1 flex-wrap">{c.services.map((s) => <span key={s} className="ll-pill" style={{ background: 'var(--accent-tint)', color: 'var(--accent-deep)' }}>{s}</span>)}</div>
          </div>
        )}
        <div className="flex gap-2 mt-3 flex-wrap"><Chip>{c.vessel_name}</Chip><Chip>{c.vessel_type}</Chip><Chip>up to {c.max_passengers}</Chip>{c.insurance_verified ? <Chip tone="accent">insured &amp; verified</Chip> : null}</div>

        {c.boat_photos?.length > 0 && (
          <div className="mt-4">
            <div className="ll-label mb-2">The boat</div>
            <div className="flex gap-2 overflow-x-auto">{c.boat_photos.map((src, i) => <img key={i} src={src} alt="" style={{ height: 120, borderRadius: 12, objectFit: 'cover', flex: 'none' }} />)}</div>
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--line)' }} className="mt-4 pt-4">
          <div className="ll-label mb-2">Reviews ({c.reviews.length})</div>
          <div className="space-y-2">
            {c.reviews.length === 0 && <div style={{ color: 'var(--ink-faint)' }} className="text-sm">No reviews yet.</div>}
            {c.reviews.map((r) => (
              <div key={r.id} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }} className="rounded-xl p-3">
                <div className="flex justify-between"><span style={{ fontWeight: 600, fontSize: 13.5 }}>{r.author}</span><span style={{ color: 'var(--accent)' }} className="tabular">{stars(r.stars)}</span></div>
                {r.comment && <div style={{ color: 'var(--ink-soft)', fontSize: 13 }} className="mt-1">{r.comment}</div>}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- CAPTAIN CONSOLE ---------------- */
function CaptainConsole({ captains, places, onChange, onCaptainClick, lockedId }) {
  const [id, setId] = useState(lockedId || '');
  const [rides, setRides] = useState([]);
  const [routePath, setRoutePath] = useState(null);
  const me = captains.find((c) => c.id === id);
  useEffect(() => { if (lockedId) setId(lockedId); else if (captains[0] && !id) setId(captains[0].id); }, [captains, lockedId]);
  useEffect(() => { const t = setInterval(() => api.rides().then(setRides), 2000); api.rides().then(setRides); return () => clearInterval(t); }, []);
  const myRide = rides.find((r) => r.captain_id === id && !['completed', 'cancelled'].includes(r.status));
  useEffect(() => { if (!myRide) { setRoutePath(null); return; } api.route({ pickup: { lat: myRide.pickup_lat, lng: myRide.pickup_lng }, dropoff: { lat: myRide.dropoff_lat, lng: myRide.dropoff_lng } }).then((r) => setRoutePath(r.path)); }, [myRide?.id]);
  const [rateRider, setRateRider] = useState(null);
  const toggle = async () => { await api.setStatus(id, { online: !me.online, lat: me.base_lat, lng: me.base_lng }); onChange(); };
  const advance = async () => {
    const finishing = myRide.status === 'in_progress';
    const rd = myRide;
    await api.advance(myRide.id);
    if (finishing) setRateRider({ rideId: rd.id, riderId: rd.rider_id, riderName: rd.rider_name || 'your rider' });
    api.rides().then(setRides); onChange();
  };
  const upcoming = rides.filter((r) => r.captain_id === id && r.status === 'scheduled');
  const nextLabel = myRide && (myRide.status === 'offered' ? 'Accept ride' : myRide.status === 'in_progress' ? 'Complete & drop off' : 'Next · ' + STAGE_LABEL[STAGES[STAGES.indexOf(myRide.status) + 1]]);
  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-4 items-start">
      <div className="space-y-4">
        <Card title={lockedId && me ? me.business_name : 'Captain console'}>
          {!lockedId && <select className="ll-select mb-4" value={id} onChange={(e) => setId(e.target.value)}>{captains.map((c) => <option key={c.id} value={c.id}>{c.business_name} · {c.status}</option>)}</select>}
          {me && (
            <div className="flex items-center justify-between">
              <div><span className="ll-pill" style={me.online ? { background: 'var(--good-tint)', color: '#0a6b49' } : { background: 'var(--surface-2)', color: 'var(--ink-faint)' }}>● {me.online ? 'Online' : 'Offline'}</span><div style={{ color: 'var(--ink-soft)', fontSize: 13 }} className="mt-1.5 tabular">{me.rides_completed} rides · ⭐ {me.rating}</div></div>
              <button onClick={toggle} disabled={me.status !== 'approved'} className={`ll-btn ${me.online ? 'll-btn-ghost' : 'll-btn-primary'}`}>{me.online ? 'Go offline' : 'Go online'}</button>
            </div>
          )}
          {me && me.status !== 'approved' && <div style={{ color: 'var(--accent-deep)' }} className="text-sm mt-2">Pending verification — you can go online once approved.</div>}
        </Card>
        <Card title="Active ride">
          {myRide ? (<>
            <div className="text-sm mb-1"><b>{myRide.pickup_label}</b></div>
            <div style={{ color: 'var(--ink-soft)' }} className="text-sm mb-3">↓ {myRide.dropoff_label}</div>
            <div className="flex gap-2 mb-3 flex-wrap"><Chip>{myRide.tier_name || 'Standard'}</Chip><Chip>{myRide.party_size} pax</Chip><Chip tone="accent">cut {money(myRide.captain_payout)}</Chip></div>
            <button onClick={advance} className="ll-btn ll-btn-primary w-full">{nextLabel}</button>
          </>) : <div style={{ color: 'var(--ink-faint)' }} className="text-sm">{me?.online ? 'Waiting for a ride request…' : 'Go online to receive requests.'}</div>}
          {upcoming.length > 0 && <div style={{ borderTop: '1px solid var(--line)' }} className="mt-3 pt-3"><div className="ll-label mb-1.5">Upcoming scheduled</div>{upcoming.map((r) => <div key={r.id} className="text-sm" style={{ color: 'var(--ink-soft)' }}>{new Date(r.scheduled_for).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {r.pickup_label} → {r.dropoff_label}</div>)}</div>}
        </Card>
        {rateRider && <RateRider info={rateRider} captainName={me?.business_name} onDone={() => setRateRider(null)} />}
      </div>
      <div className="ll-card !p-0 overflow-hidden">
        <LakeMap places={places} captains={captains} activeCaptainId={id} route={routePath} onCaptainClick={onCaptainClick}
          pickup={myRide && { lat: myRide.pickup_lat, lng: myRide.pickup_lng, label: myRide.pickup_label }}
          dropoff={myRide && { lat: myRide.dropoff_lat, lng: myRide.dropoff_lng, label: myRide.dropoff_label }} height="calc(100vh - 190px)" />
      </div>
    </div>
  );
}

function RateRider({ info, captainName, onDone }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [done, setDone] = useState(false);
  const submit = async (n) => {
    setStars(n);
    await api.addRiderReview(info.riderId, { author: captainName || 'Captain', stars: n, comment, ride_id: info.rideId });
    setDone(true);
  };
  return (
    <Card title="Rate your rider">
      {done ? <div style={{ color: 'var(--good)' }} className="text-sm">Thanks! Your rating of {info.riderName} was saved. <button onClick={onDone} className="underline">Close</button></div> : (<>
        <div style={{ color: 'var(--ink-soft)', fontSize: 13 }} className="mb-2">How was {info.riderName}?</div>
        <div className="flex gap-1 mb-2">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setStars(n)} style={{ fontSize: 26, color: n <= stars ? 'var(--accent)' : 'var(--line-strong)' }}>★</button>)}</div>
        <textarea className="ll-input mb-2" rows={2} placeholder="Optional note (on time, respectful, etc.)" value={comment} onChange={(e) => setComment(e.target.value)} />
        <div className="flex gap-2">
          <button onClick={onDone} className="ll-btn ll-btn-ghost flex-1">Skip</button>
          <button onClick={() => submit(stars || 5)} disabled={stars === 0} className="ll-btn ll-btn-primary flex-1">Submit rating</button>
        </div>
      </>)}
    </Card>
  );
}

/* ---------------- BECOME A CAPTAIN ---------------- */
const REQS = [
  { icon: '🎓', title: 'USCG OUPV (“6-pack”) license', short: 'Carry up to 6 paying passengers — the core credential for a for-hire captain.',
    detail: [
      ['What it is', 'The OUPV (Operator of Uninspected Passenger Vessels), commonly called the “six-pack,” legally lets you carry up to six paying passengers on the lake. It’s the minimum credential to run rides for hire.'],
      ['Who qualifies', 'Be at least 18. Have 360 days of documented boating experience, with 90 of those days in the last 7 years. Pass a USCG-approved medical exam and enroll in a random drug-testing program.'],
      ['The exam', 'You’ll pass a USCG test covering navigation rules, deck & safety, and basic chart plotting. Most people take a USCG-approved prep course (in person or online).'],
      ['How to get it', 'Take an approved course, collect your sea-service form and character references, then apply to the National Maritime Center. Budget roughly 2–3 months and a few hundred dollars in fees.'],
      ['Drug-testing program', 'You must enroll in a USCG-approved random drug-testing consortium (a solo operator can’t test themselves). They handle random selection, the DOT 5-panel test, MRO review, and give you the consortium letter for your license packet — roughly $100–200/year. Note: marijuana still fails, even where state-legal.'],
      ['Also required', 'A TWIC card (Transportation Worker Identification Credential) from TSA, and a merchant-mariner medical exam (Form CG-719K), valid 2 years.'],
    ],
    links: [
      ['📘 OUPV course — Mariners Learning System', 'https://marinerslearningsystem.com/products/captains-license-oupv-six-pack'],
      ['📘 OUPV course — Sea School', 'https://www.seaschool.com/courses/captains-license-oupv'],
      ['🩺 Medical exam (CG-719K) — find a VA clinic', 'https://bluehive.com/states/virginia/services/mariner-physical/'],
      ['🩺 Medical exam — Concentra locations', 'https://www.concentra.com/urgent-care-centers/'],
      ['🧪 Drug-testing consortium — Maritime Consortium', 'https://drugfreevessel.com/join/'],
      ['🧪 Drug-testing consortium — Accredited Drug Testing', 'https://www.accrediteddrugtesting.com/uscg-drug-testing/'],
      ['🪪 TWIC card — TSA enrollment locator', 'https://tsaenrollmentbyidemia.tsa.dhs.gov/programs/twic'],
      ['🪪 TWIC in Roanoke — IDEMIA / DMV locations', 'https://www.identogo.com/locations/virginia'],
      ['⚓ Apply / renew — National Maritime Center', 'https://www.dco.uscg.mil/nmc/'],
    ] },
  { icon: '🛟', title: 'Commercial charter insurance', short: 'A personal boat policy won’t cover passengers-for-hire — you need commercial coverage.',
    detail: [
      ['Why you need it', 'Standard recreational boat policies exclude carrying passengers for hire. If you take paying riders on a personal policy, a claim will likely be denied.'],
      ['What to get', 'A commercial/charter marine liability policy — typically $1M+ liability — plus hull coverage for your boat. Ask your agent to name DockDash as an additional insured.'],
      ['Cost', 'Varies by boat value and passenger count; many small-charter policies land in the low four figures per year. Shop marine-specialty insurers.'],
    ] },
  { icon: '🚤', title: 'A lake-ready vessel', short: 'A well-maintained boat that seats up to 6, with current safety equipment.',
    detail: [
      ['The boat', 'Any well-kept vessel that comfortably and legally seats up to 6 passengers — pontoon, deck boat, bowrider, or center console.'],
      ['Required safety gear', 'A Coast Guard-approved life jacket for every person aboard, a throwable flotation device, fire extinguisher(s), visual distress signals, a sound-producing device, and navigation lights.'],
      ['Upkeep', 'Keep the boat clean and mechanically sound. Riders rate you after every trip, and a solid, tidy boat earns repeat business.'],
    ] },
  { icon: '✅', title: 'Register & get verified', short: 'Submit your details below — we verify before you can go online.',
    detail: [
      ['What we collect', 'Your contact info (with a verified email), license type & number, insurance details, vessel info, and photos of your boat.'],
      ['Verification', 'Our team reviews your license and insurance before activating your account. Riders only ever see approved captains.'],
      ['Then you’re live', 'Once approved, flip yourself “online” whenever you want to accept rides. Keep 85% of every fare, set your own hours.'],
    ] },
  { icon: '🚢', title: 'Want to carry more than 6?', short: 'Bigger group charters need a Master license AND an inspected vessel — here’s the path.',
    detail: [
      ['The six-passenger rule', 'The OUPV “six-pack” legally caps you at 6 paying passengers. To carry 7 or more you need two things together: a higher license and an inspected boat.'],
      ['A Master license', 'A Master license (25, 50, or 100 gross tons — matched to your vessel’s size) authorizes more passengers. It builds on the OUPV path with more documented sea time and an expanded exam.'],
      ['An inspected vessel (COI)', 'Beyond 6 passengers the boat itself must pass USCG inspection and carry a Certificate of Inspection — Subchapter T “small passenger vessel,” up to 150 passengers. Inspection covers construction, stability, and safety equipment.'],
      ['Is it worth it?', 'For get-home shuttle runs, the six-pack is plenty and much simpler. Pursue a Master + inspected vessel only if you want to run larger group charters, tours, or events. DockDash shuttle rides are capped at 6, but larger operators are welcome as we add group charters.'],
    ],
    links: [
      ['📘 Master license courses — Mariners Learning System', 'https://marinerslearningsystem.com/collections/captains-license-coast-guard-approved-courses'],
      ['📘 Master license course — Sea School', 'https://www.seaschool.com/'],
      ['⚓ Vessel inspection & COI — USCG', 'https://www.dco.uscg.mil/nmc/'],
    ] },
];
function PhotoCapture({ label, value, onChange, multiple }) {
  const onFile = async (e) => {
    const files = [...e.target.files]; if (!files.length) return;
    const urls = await Promise.all(files.map((f) => fileToDataUrl(f)));
    onChange(multiple ? [...(value || []), ...urls] : urls[0]);
    e.target.value = '';
  };
  const list = multiple ? (value || []) : (value ? [value] : []);
  return (
    <div>
      <span className="ll-label">{label}</span>
      <div className="flex gap-2 flex-wrap mt-1 items-center">
        {list.map((src, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <img src={src} alt="" style={{ height: 72, width: 72, borderRadius: 10, objectFit: 'cover' }} />
            <button onClick={() => onChange(multiple ? value.filter((_, k) => k !== i) : '')} style={{ position: 'absolute', top: -6, right: -6, background: '#c0392b', color: '#fff', borderRadius: '50%', width: 20, height: 20, fontSize: 12 }}>✕</button>
          </div>
        ))}
        <label className="ll-btn ll-btn-ghost" style={{ cursor: 'pointer' }}>
          📷 {list.length ? 'Add' : 'Take / choose'} photo
          <input type="file" accept="image/*" capture="environment" multiple={multiple} style={{ display: 'none' }} onChange={onFile} />
        </label>
      </div>
    </div>
  );
}
function BecomeCaptain({ onDone }) {
  const [phase, setPhase] = useState('learn');
  const [step, setStep] = useState(0);
  const [detail, setDetail] = useState(null);
  const [f, setF] = useState({ v_pax: 6, uscg_license_type: LICENSE_TYPES[0].value, boat_photos: [] });
  const [emailVerified, setEmailVerified] = useState(false);
  const [codeSent, setCodeSent] = useState('');
  const [code, setCode] = useState('');
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const sendCode = async () => { const r = await api.verifyStart(f.email || ''); if (r.sent) setCodeSent(r.devCode || 'sent'); };
  const checkCode = async () => { const r = await api.verifyCheck(f.email || '', code); setEmailVerified(r.verified); };

  if (phase === 'done') return (
    <Card style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
      <div className="text-4xl mb-2">🎉</div>
      <h2 className="ll-title" style={{ fontSize: 22 }}>Application submitted</h2>
      <p style={{ color: 'var(--ink-soft)' }} className="mt-2">Thanks, {f.name || 'Captain'}! Your application was sent for review and the team was notified. You’ll appear to riders once your license &amp; insurance are verified.</p>
      <button className="ll-btn ll-btn-primary mt-4" onClick={() => { setPhase('learn'); setStep(0); setF({ v_pax: 6, uscg_license_type: LICENSE_TYPES[0].value, boat_photos: [] }); setEmailVerified(false); setCodeSent(''); setCode(''); }}>Done</button>
    </Card>
  );

  if (phase === 'learn' && detail !== null) {
    const r = REQS[detail];
    return (
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <button onClick={() => setDetail(null)} className="ll-btn ll-btn-ghost mb-3" style={{ padding: '6px 12px' }}>← What it takes</button>
        <Card>
          <div className="flex items-center gap-2 mb-3"><span style={{ fontSize: 30 }}>{r.icon}</span><h2 className="ll-title" style={{ fontSize: 22 }}>{r.title}</h2></div>
          <div className="space-y-4">
            {r.detail.map(([h, body], i) => (
              <div key={i}><div className="ll-label" style={{ fontSize: 12 }}>{h}</div><p style={{ color: 'var(--ink)', lineHeight: 1.6 }} className="mt-1">{body}</p></div>
            ))}
          </div>
          {r.links && (
            <div className="mt-4" style={{ borderTop: '1px solid var(--line)', paddingTop: 16 }}>
              <div className="ll-label mb-2">Where to go — courses, medical, drug testing &amp; TWIC</div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {r.links.map(([label, url]) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="rounded-lg px-3 py-2" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--brand-deep)', fontSize: 13, fontWeight: 600 }}>{label} ↗</a>
                ))}
              </div>
              <div style={{ color: 'var(--ink-faint)', fontSize: 11.5 }} className="mt-2">Third-party providers, listed for convenience — confirm current requirements with the National Maritime Center.</div>
            </div>
          )}
          <button className="ll-btn ll-btn-primary mt-5 w-full" onClick={() => { setDetail(null); setPhase('wizard'); }}>Start registration →</button>
        </Card>
      </div>
    );
  }

  if (phase === 'learn') return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div className="ll-card ll-card-pad mb-3" style={{ background: 'var(--grad-brand)', color: '#fff', border: 'none' }}>
        <h2 className="ll-title" style={{ fontSize: 26 }}>Captain with DockDash</h2>
        <p style={{ color: 'rgba(255,255,255,.85)' }} className="mt-1">Turn your boat into income. Set your own hours, accept rides when you want, and keep 85% of every fare. Here’s what it takes on Smith Mountain Lake — tap any card to learn more.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {REQS.map((r, i) => (
          <button key={i} onClick={() => setDetail(i)} className="ll-card ll-card-pad text-left" style={{ display: 'block' }}>
            <div className="flex items-center gap-2 mb-1"><span style={{ fontSize: 22 }}>{r.icon}</span><span style={{ fontWeight: 700 }}>{r.title}</span></div>
            <div style={{ color: 'var(--ink-soft)', fontSize: 13.5, lineHeight: 1.5 }}>{r.short}</div>
            <div style={{ color: 'var(--brand)', fontSize: 13, fontWeight: 600 }} className="mt-2">Learn more →</div>
          </button>
        ))}
      </div>
      <div className="ll-card ll-card-pad mt-3 flex items-center justify-between flex-wrap gap-2">
        <div><div style={{ fontWeight: 700 }}>Ready to register?</div><div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>Takes about 3 minutes. We’ll walk you through each step.</div></div>
        <button className="ll-btn ll-btn-primary" onClick={() => setPhase('wizard')}>Start registration →</button>
      </div>
    </div>
  );

  const STEPS = ['About you', 'Your license', 'Insurance', 'Your vessel', 'Photos', 'Profile'];
  const canNext = step === 0 ? (f.name && f.email) : true;
  const submit = async () => {
    await api.registerCaptain({
      name: f.name, business_name: f.business_name, email: f.email, phone: f.phone, email_verified: emailVerified,
      uscg_license_type: f.uscg_license_type, uscg_license_no: f.uscg_license_no, license_expiry: f.license_expiry,
      insurer: f.insurer, policy_no: f.policy_no, home_marina: f.home_marina, bio: f.bio, website: f.website,
      photo_url: f.photo_url, boat_photos: f.boat_photos, services: f.services || [],
      vessel: { name: f.v_name, type: f.v_type, max_passengers: Math.min(49, +f.v_pax || 6), length_ft: +f.v_len || 0, cruise_speed_kts: 22 },
    });
    onDone?.(); setPhase('done');
  };

  return (
    <Card style={{ maxWidth: 560, margin: '0 auto' }}>
      <div className="flex gap-1.5 mb-4">{STEPS.map((s, i) => <div key={s} style={{ height: 4, borderRadius: 2, flex: 1, background: i <= step ? 'var(--brand)' : 'var(--line-strong)' }} />)}</div>
      <div className="ll-label mb-1">Step {step + 1} of {STEPS.length}</div>
      <h2 className="ll-title mb-3" style={{ fontSize: 20 }}>{STEPS[step]}</h2>

      <div className="space-y-3">
        {step === 0 && (<>
          <Field label="Your name"><input className="ll-input" value={f.name || ''} onChange={(e) => upd('name', e.target.value)} /></Field>
          <Field label="Business name"><input className="ll-input" value={f.business_name || ''} onChange={(e) => upd('business_name', e.target.value)} /></Field>
          <Field label="Email"><input className="ll-input" type="email" value={f.email || ''} onChange={(e) => { upd('email', e.target.value); setEmailVerified(false); setCodeSent(''); }} placeholder="you@example.com" /></Field>
          <Field label="Phone"><input className="ll-input tabular" inputMode="tel" value={f.phone || ''} onChange={(e) => upd('phone', formatPhone(e.target.value))} placeholder="(540) 555-0100" /></Field>
        </>)}

        {step === 1 && (<>
          <Field label="License type">
            <select className="ll-select" value={f.uscg_license_type} onChange={(e) => { upd('uscg_license_type', e.target.value); upd('uscg_license_no', formatLicenseNo(e.target.value, f.uscg_license_no)); }}>
              {LICENSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.value}</option>)}
            </select>
          </Field>
          <Field label="License number"><input className="ll-input tabular" value={f.uscg_license_no || ''} onChange={(e) => upd('uscg_license_no', formatLicenseNo(f.uscg_license_type, e.target.value))} placeholder={`${licPrefix(f.uscg_license_type)}-0000000`} /></Field>
          <Field label="Expiry date"><input className="ll-input" type="date" value={f.license_expiry || ''} onChange={(e) => upd('license_expiry', e.target.value)} /></Field>
        </>)}

        {step === 2 && (<>
          <Field label="Insurance provider"><input className="ll-input" value={f.insurer || ''} onChange={(e) => upd('insurer', e.target.value)} /></Field>
          <Field label="Policy number"><input className="ll-input" value={f.policy_no || ''} onChange={(e) => upd('policy_no', e.target.value)} /></Field>
        </>)}

        {step === 3 && (<>
          <Field label="Boat name"><input className="ll-input" value={f.v_name || ''} onChange={(e) => upd('v_name', e.target.value)} /></Field>
          <Field label="Type"><input className="ll-input" value={f.v_type || ''} onChange={(e) => upd('v_type', e.target.value)} placeholder="pontoon, deck boat, bowrider…" /></Field>
          <div className="flex gap-2">
            <Field label="Max passengers"><input className="ll-input tabular" type="number" min="1" max="49" value={f.v_pax} onChange={(e) => upd('v_pax', e.target.value)} /></Field>
            <Field label="Length (ft)"><input className="ll-input tabular" type="number" value={f.v_len || ''} onChange={(e) => upd('v_len', e.target.value)} /></Field>
          </div>
          {(+f.v_pax || 0) > 6 && <div style={{ background: 'var(--accent-tint)', color: 'var(--accent-deep)', fontSize: 12.5 }} className="rounded-lg px-3 py-2">Carrying more than 6 requires a Master license <b>and</b> a USCG-inspected vessel (COI). See “Want to carry more than 6?” for the path.</div>}
        </>)}

        {step === 4 && (<>
          <PhotoCapture label="Boat photos (riders see these)" value={f.boat_photos} multiple onChange={(v) => upd('boat_photos', v)} />
          <div className="mt-3"><PhotoCapture label="Your photo (optional)" value={f.photo_url} onChange={(v) => upd('photo_url', v)} /></div>
        </>)}

        {step === 5 && (<>
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 12, padding: 12 }}>
            <span className="ll-label">Confirm your email</span>
            <div className="flex gap-2 mt-1 items-center flex-wrap">
              <input className="ll-input" style={{ background: '#fff', maxWidth: 240 }} value={f.email || ''} onChange={(e) => { upd('email', e.target.value); setEmailVerified(false); setCodeSent(''); }} placeholder="you@example.com" />
              {emailVerified ? <span className="ll-pill" style={{ background: 'var(--good-tint)', color: '#0a6b49' }}>✓ verified</span>
                : <button className="ll-btn ll-btn-ghost" style={{ padding: '8px 14px' }} onClick={sendCode} disabled={!f.email}>Send code</button>}
            </div>
            {codeSent && !emailVerified && (
              <div className="mt-2 flex gap-2 items-center flex-wrap">
                <input className="ll-input tabular" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} style={{ maxWidth: 140 }} />
                <button className="ll-btn ll-btn-primary" style={{ padding: '8px 14px' }} onClick={checkCode}>Verify</button>
                <span style={{ color: 'var(--ink-faint)', fontSize: 12 }}>demo code: {codeSent}</span>
              </div>
            )}
          </div>
          <div>
            <span className="ll-label">Services you offer</span>
            <div className="flex gap-1.5 mt-1 flex-wrap">
              {ALL_SERVICES.map((s) => {
                const on = (f.services || []).includes(s);
                return <button key={s} type="button" onClick={() => upd('services', on ? f.services.filter((x) => x !== s) : [...(f.services || []), s])}
                  className="ll-pill" style={{ background: on ? 'var(--brand)' : 'var(--surface-2)', color: on ? '#fff' : 'var(--ink-soft)', cursor: 'pointer', border: '1px solid var(--line-strong)' }}>{s}</button>;
              })}
            </div>
          </div>
          <Field label="Home marina"><input className="ll-input" value={f.home_marina || ''} onChange={(e) => upd('home_marina', e.target.value)} /></Field>
          <Field label="Short bio (shown on your profile)"><textarea className="ll-input" rows={3} value={f.bio || ''} onChange={(e) => upd('bio', e.target.value)} /></Field>
          <Field label="Website / Facebook (optional)"><input className="ll-input" value={f.website || ''} onChange={(e) => upd('website', e.target.value)} /></Field>
        </>)}
      </div>

      {step === STEPS.length - 1 && !emailVerified && <div style={{ color: 'var(--accent-deep)', fontSize: 12.5 }} className="mt-2">Confirm your email above to submit your application.</div>}
      <div className="flex gap-2 mt-4">
        <button className="ll-btn ll-btn-ghost" onClick={() => step === 0 ? setPhase('learn') : setStep(step - 1)}>Back</button>
        {step < STEPS.length - 1
          ? <button className="ll-btn ll-btn-primary flex-1" disabled={!canNext} onClick={() => setStep(step + 1)}>Next</button>
          : <button className="ll-btn ll-btn-primary flex-1" disabled={!emailVerified} onClick={submit}>Submit application</button>}
      </div>
    </Card>
  );
}
function Field({ label, children }) {
  return <label className="block"><span className="ll-label">{label}</span><div className="mt-1">{children}</div></label>;
}

/* ---------------- ADMIN ---------------- */
function AdminView({ onOpenCaptain, onOpenRider }) {
  const [tab, setTab] = useState('applications');
  const [rides, setRides] = useState([]);
  const [apps, setApps] = useState([]);
  const [caps, setCaps] = useState([]);
  const load = () => { api.rides().then(setRides); api.applications().then(setApps); api.captains().then(setCaps); };
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, []);
  const completed = rides.filter((r) => r.status === 'completed');
  const revenue = completed.reduce((s, r) => s + (r.platform_fee || 0), 0);
  const pending = apps.filter((a) => a.status === 'pending').length;
  const TABS = [['applications', `Applications${pending ? ` (${pending})` : ''}`], ['users', 'Users'], ['access', 'Access'], ['analytics', 'Analytics'], ['pricing', 'Pricing tiers'], ['history', 'Ride history'], ['overview', 'Overview']];
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">{TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`ll-btn ${tab === k ? 'll-btn-primary' : 'll-btn-ghost'}`} style={{ padding: '8px 14px' }}>{l}</button>)}</div>
      {tab === 'applications' && <Applications apps={apps} onChange={load} />}
      {tab === 'users' && <Users captains={caps} onOpenCaptain={onOpenCaptain} onOpenRider={onOpenRider} />}
      {tab === 'access' && <AccessManager />}
      {tab === 'analytics' && <Analytics rides={rides} captains={caps} />}
      {tab === 'pricing' && <PricingTiers />}
      {tab === 'history' && <RideHistory rides={rides} />}
      {tab === 'overview' && (
        <Card title="Platform overview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Captains" value={caps.filter((c) => c.status === 'approved').length} />
            <Stat label="Online now" value={caps.filter((c) => c.online).length} />
            <Stat label="Applications" value={pending} />
            <Stat label="Completed rides" value={completed.length} />
          </div>
          <div style={{ background: 'var(--good-tint)' }} className="rounded-xl p-4 text-center">
            <div className="ll-title tabular" style={{ fontSize: 32, color: '#0a6b49' }}>{money(revenue)}</div>
            <div style={{ color: '#0a6b49', fontSize: 12 }}>platform commission earned (completed rides)</div>
          </div>
        </Card>
      )}
    </div>
  );
}
function Applications({ apps, onChange }) {
  const approve = async (id) => { await api.approveCaptain(id); onChange(); };
  const reject = async (id) => { await api.rejectCaptain(id); onChange(); };
  return (
    <Card title="Captain applications" right={<span style={{ color: 'var(--ink-faint)', fontSize: 12 }}>notified → rob@blackwell-mountain.com</span>}>
      {apps.length === 0 ? <div style={{ color: 'var(--ink-faint)' }} className="text-sm">No applications yet.</div> : (
        <div className="space-y-3">
          {apps.map((a) => (
            <div key={a.id} style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }} className="rounded-xl p-3.5">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-start gap-3">
                  {a.photo_url && <img src={a.photo_url} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover' }} />}
                  <div>
                    <div style={{ fontWeight: 700 }}>{a.business_name || a.name} {a.status === 'rejected' && <span className="ll-pill" style={{ background: '#fde8e8', color: '#c0392b' }}>rejected</span>}</div>
                    <div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>{a.name} · {a.phone}</div>
                    <div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>{a.email} {a.email_verified ? <span style={{ color: 'var(--good)' }}>✓ verified</span> : <span style={{ color: 'var(--accent-deep)' }}>unverified</span>}</div>
                    <div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>{a.uscg_license_type} · #{a.uscg_license_no} · exp {a.license_expiry || '—'}</div>
                    <div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>Insurance: {a.insurer || '—'} #{a.policy_no || '—'}</div>
                    <div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>Vessel: {a.vessel_name} ({a.vessel_type}, {a.max_passengers} pax)</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="ll-btn ll-btn-ghost" style={{ padding: '7px 12px' }} onClick={() => reject(a.id)}>Reject</button>
                  <button className="ll-btn ll-btn-primary" style={{ padding: '7px 12px' }} onClick={() => approve(a.id)}>Approve</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
const ROLE_LABEL = { owner: 'Global admin', admin: 'Admin' };
function AccessManager() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', phone: '', role: 'admin' });
  const [err, setErr] = useState('');
  const load = () => api.admins().then(setData).catch(() => setData({ admins: [], me: {} }));
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const isOwner = data.me?.role === 'owner';
  const add = async () => {
    setErr('');
    const r = await api.addAdmin(form);
    if (r?.error) { setErr(r.error); return; }
    setForm({ email: '', name: '', phone: '', role: 'admin' }); load();
  };
  const save = async (a, patch) => { const r = await api.updateAdmin(a.id, { ...a, ...patch }); if (r?.error) setErr(r.error); load(); };
  const remove = async (a) => { const r = await api.removeAdmin(a.id); if (r?.error) setErr(r.error); load(); };

  return (
    <Card title="Access — admins &amp; users"
      right={<span className="ll-pill" style={{ background: isOwner ? 'var(--accent-tint)' : 'var(--surface-2)', color: isOwner ? 'var(--accent-deep)' : 'var(--ink-soft)' }}>you are {ROLE_LABEL[data.me?.role] || 'Admin'}</span>}>
      {!isOwner && (
        <div style={{ background: 'var(--surface-2)', color: 'var(--ink-soft)' }} className="rounded-xl px-3 py-2 text-sm mb-3">
          You can see who has access, but only the <b>global admin</b> can add, edit, or remove admins.
        </div>
      )}

      <div className="space-y-2">
        {data.admins.map((a) => (
          <div key={a.id} style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }} className="rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
            <div style={{ minWidth: 190 }}>
              <div style={{ fontWeight: 600 }}>{a.name || a.email}
                <span className="ll-pill ml-2" style={a.role === 'owner'
                  ? { background: 'var(--accent-tint)', color: 'var(--accent-deep)' }
                  : { background: 'var(--brand-tint)', color: 'var(--brand-deep)' }}>{ROLE_LABEL[a.role]}</span>
                {!a.active && <span className="ll-pill ml-1" style={{ background: '#fde8e8', color: '#c0392b' }}>disabled</span>}
              </div>
              <div style={{ color: 'var(--ink-soft)', fontSize: 12.5 }}>{a.email}{a.phone ? ` · ${a.phone}` : ''}</div>
            </div>
            {isOwner && (
              <div className="flex items-center gap-2 flex-wrap">
                <select className="ll-select" style={{ padding: '6px 8px', width: 130 }} value={a.role}
                  onChange={(e) => save(a, { role: e.target.value })}>
                  <option value="admin">Admin</option><option value="owner">Global admin</option>
                </select>
                <button className="ll-btn ll-btn-ghost" style={{ padding: '6px 10px' }} onClick={() => save(a, { active: a.active ? 0 : 1 })}>
                  {a.active ? 'Disable' : 'Enable'}
                </button>
                <button className="ll-btn ll-btn-ghost" style={{ padding: '6px 10px', color: '#c0392b' }} onClick={() => remove(a)}>Remove</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {isOwner && (
        <div style={{ borderTop: '1px solid var(--line)' }} className="mt-4 pt-4">
          <div className="ll-label mb-2">Grant access to someone new</div>
          <div className="flex gap-2 flex-wrap items-end">
            <input className="ll-input" style={{ maxWidth: 220 }} placeholder="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className="ll-input" style={{ maxWidth: 170 }} placeholder="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="ll-input" style={{ maxWidth: 150 }} placeholder="phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} />
            <select className="ll-select" style={{ width: 140 }} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="admin">Admin</option><option value="owner">Global admin</option>
            </select>
            <button className="ll-btn ll-btn-primary" disabled={!form.email.trim()} onClick={add}>Add</button>
          </div>
          <div style={{ color: 'var(--ink-faint)', fontSize: 12 }} className="mt-2">
            They sign in with this email — no password. <b>Admins</b> manage captains, pricing and rides. <b>Global admins</b> can also manage access.
          </div>
        </div>
      )}
      {err && <div style={{ color: '#c0392b', fontSize: 13 }} className="mt-2">{err}</div>}
    </Card>
  );
}

function Users({ captains, onOpenCaptain, onOpenRider }) {
  const [seg, setSeg] = useState('riders');
  const [riders, setRiders] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => { api.riders().then(setRiders); const t = setInterval(() => api.riders().then(setRiders), 4000); return () => clearInterval(t); }, []);
  const term = q.trim().toLowerCase();
  const rlist = riders.filter((r) => !term || `${r.name} ${r.email} ${r.phone}`.toLowerCase().includes(term));
  const clist = captains.filter((c) => !term || `${c.business_name} ${c.name} ${c.email}`.toLowerCase().includes(term));
  const th = { padding: '8px 10px', borderBottom: '1px solid var(--line)', fontWeight: 600, textAlign: 'left', color: 'var(--ink-soft)', fontSize: 11.5 };
  const td = { padding: '9px 10px', whiteSpace: 'nowrap' };

  return (
    <Card title="Users" right={<input className="ll-input" placeholder="🔍 Search…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 220 }} />}>
      <div className="flex gap-2 mb-3">
        {[['riders', `Riders (${riders.length})`], ['captains', `Captains (${captains.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setSeg(k)} className="ll-pill" style={{ background: seg === k ? 'var(--brand)' : 'var(--surface-2)', color: seg === k ? '#fff' : 'var(--ink-soft)', cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        {seg === 'riders' ? (
          <table className="w-full text-sm" style={{ minWidth: 560, borderCollapse: 'collapse' }}>
            <thead><tr>{['Name', 'Email', 'Phone', 'Rating', 'Rides', 'Joined', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {rlist.map((r) => (
                <tr key={r.id} onClick={() => onOpenRider(r.id)} style={{ borderBottom: '1px solid var(--line)', cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
                  <td style={{ ...td, color: 'var(--ink-soft)' }}>{r.email}</td>
                  <td style={{ ...td, color: 'var(--ink-soft)' }}>{r.phone}</td>
                  <td style={td}><span style={{ color: 'var(--accent)' }} className="tabular">{stars(r.rating)}</span> <span style={{ color: 'var(--ink-soft)' }}>{r.rating}</span></td>
                  <td style={td} className="tabular">{r.completed_count}/{r.ride_count}</td>
                  <td style={{ ...td, color: 'var(--ink-soft)' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                  <td style={{ ...td, color: 'var(--brand)', fontWeight: 600 }}>View →</td>
                </tr>
              ))}
              {rlist.length === 0 && <tr><td colSpan={7} style={{ ...td, color: 'var(--ink-faint)' }}>No riders.</td></tr>}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm" style={{ minWidth: 560, borderCollapse: 'collapse' }}>
            <thead><tr>{['Business', 'Captain', 'Status', 'Rating', 'Rides', 'License'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {clist.map((c) => (
                <tr key={c.id} onClick={() => onOpenCaptain(c.id)} style={{ borderBottom: '1px solid var(--line)', cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ ...td, fontWeight: 600 }}>{c.business_name}</td>
                  <td style={{ ...td, color: 'var(--ink-soft)' }}>{c.name}</td>
                  <td style={td}><span className="ll-pill" style={{ background: c.status === 'approved' ? 'var(--good-tint)' : 'var(--accent-tint)', color: c.status === 'approved' ? '#0a6b49' : 'var(--accent-deep)' }}>{c.status}</span></td>
                  <td style={td}><span style={{ color: 'var(--accent)' }} className="tabular">{stars(c.rating)}</span> <span style={{ color: 'var(--ink-soft)' }}>{c.rating}</span></td>
                  <td style={td} className="tabular">{c.rides_completed}</td>
                  <td style={{ ...td, color: 'var(--ink-soft)' }}>{c.uscg_license_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}
function RiderProfile({ id, onClose }) {
  const [r, setR] = useState(null);
  const [rides, setRides] = useState([]);
  useEffect(() => { api.rider(id).then(setR); api.rides().then((all) => setRides(all.filter((x) => x.rider_id === id))); }, [id]);
  if (!r) return null;
  const completed = rides.filter((x) => x.status === 'completed');
  const spent = completed.reduce((s, x) => s + (x.fare_final ?? x.fare_estimate ?? 0), 0);
  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <button onClick={onClose} className="ll-btn ll-btn-ghost mb-3" style={{ padding: '6px 12px' }}>✕ Close</button>
      <Card>
        <h2 className="ll-title" style={{ fontSize: 22 }}>{r.name}</h2>
        <div style={{ color: 'var(--ink-soft)' }}>{r.email} · {r.phone}</div>
        <div style={{ color: 'var(--accent)', fontSize: 16 }} className="tabular mt-1">{stars(r.rating)} <span style={{ color: 'var(--ink-soft)' }}>{r.rating}</span></div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <Stat label="Rides" value={completed.length} />
          <Stat label="Total spent" value={money(spent)} />
          <Stat label="Reviews" value={r.reviews.length} />
        </div>
        <div style={{ borderTop: '1px solid var(--line)' }} className="mt-4 pt-4">
          <div className="ll-label mb-2">Captain reviews ({r.reviews.length})</div>
          {r.reviews.length === 0 ? <div style={{ color: 'var(--ink-faint)' }} className="text-sm">No reviews yet.</div> :
            <div className="space-y-2">{r.reviews.map((rv) => (
              <div key={rv.id} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }} className="rounded-xl p-2.5 text-sm">
                <div className="flex justify-between"><b>{rv.author}</b><span style={{ color: 'var(--accent)' }}>{stars(rv.stars)}</span></div>
                {rv.comment && <div style={{ color: 'var(--ink-soft)' }} className="mt-0.5">{rv.comment}</div>}
              </div>
            ))}</div>}
        </div>
        <div style={{ borderTop: '1px solid var(--line)' }} className="mt-4 pt-4">
          <div className="ll-label mb-2">Recent rides ({rides.length})</div>
          <div className="space-y-1.5">
            {rides.slice(0, 8).map((x) => (
              <div key={x.id} className="flex justify-between text-sm" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 5 }}>
                <span>{x.pickup_label} → {x.dropoff_label} <span style={{ color: 'var(--ink-faint)' }}>· {x.business_name || '—'}</span></span>
                <span className="tabular" style={{ fontWeight: 600 }}>{money(x.fare_final ?? x.fare_estimate)}</span>
              </div>
            ))}
            {rides.length === 0 && <div style={{ color: 'var(--ink-faint)' }} className="text-sm">No rides yet.</div>}
          </div>
        </div>
      </Card>
    </div>
  );
}
const TIER_FIELDS = [['base_fare', 'Base $'], ['per_nm', 'Per nm $'], ['per_min', 'Per min $'], ['minimum_fare', 'Min $'], ['night_multiplier', 'Night ×'], ['night_start_hour', 'Night hr'], ['platform_fee_pct', 'Cut 0–1']];
function PricingTiers() {
  const [tiers, setTiers] = useState([]);
  const load = () => api.tiers().then(setTiers);
  useEffect(() => { load(); }, []);
  const patch = (id, k, v) => setTiers((ts) => ts.map((t) => (t.id === id ? { ...t, [k]: v } : t)));
  const save = (t) => api.updateTier(t.id, t).then(load);
  const add = () => api.createTier({ name: 'New tier', blurb: '', sort: tiers.length }).then(load);
  const remove = (id) => api.deleteTier(id).then(load);
  return (
    <Card title="Pricing tiers" right={<button onClick={add} className="ll-btn ll-btn-ghost" style={{ padding: '7px 12px' }}>＋ Add tier</button>}>
      <p style={{ color: 'var(--ink-soft)', fontSize: 13 }} className="mb-4">Riders pick a tier when booking. Each tier has its own rate card and platform cut.</p>
      <div className="space-y-3">
        {tiers.map((t) => (
          <div key={t.id} style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }} className="rounded-xl p-3.5">
            <div className="flex gap-2 items-center mb-2.5">
              <input className="ll-input" style={{ fontWeight: 700, flex: 1 }} value={t.name} onChange={(e) => patch(t.id, 'name', e.target.value)} />
              <label style={{ fontSize: 12, color: 'var(--ink-soft)' }} className="flex items-center gap-1.5"><input type="checkbox" checked={!!t.active} onChange={(e) => patch(t.id, 'active', e.target.checked ? 1 : 0)} /> active</label>
              <button onClick={() => remove(t.id)} style={{ color: '#c0392b' }} className="px-2">✕</button>
            </div>
            <input className="ll-input mb-2.5" placeholder="short description" value={t.blurb || ''} onChange={(e) => patch(t.id, 'blurb', e.target.value)} />
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-3">{TIER_FIELDS.map(([k, label]) => <label key={k} className="ll-label" style={{ fontWeight: 500 }}>{label}<input type="number" step="0.05" className="ll-input tabular mt-1" style={{ padding: '7px 8px' }} value={t[k]} onChange={(e) => patch(t.id, k, +e.target.value)} /></label>)}</div>
            <button onClick={() => save(t)} className="ll-btn ll-btn-primary" style={{ padding: '8px 14px' }}>Save {t.name}</button>
          </div>
        ))}
      </div>
    </Card>
  );
}
function RideHistory({ rides }) {
  const PAY = { paid: { background: 'var(--good-tint)', color: '#0a6b49' }, authorized: { background: 'var(--brand-tint)', color: 'var(--brand-deep)' }, requires_payment: { background: 'var(--accent-tint)', color: 'var(--accent-deep)' } };
  return (
    <Card title={`Ride history · ${rides.length}`}>
      {rides.length === 0 ? <div style={{ color: 'var(--ink-faint)' }} className="text-sm">No rides yet.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: 720, borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: 'var(--ink-soft)', fontSize: 11.5, textAlign: 'left' }}>{['When', 'Route', 'Captain', 'Tier', 'Pax', 'Fare', 'Cut', 'Status', 'Payment'].map((h) => <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--line)', fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>{rides.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--line)' }}>
                <td style={{ padding: '9px 10px', color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>{r.scheduled_for ? '⏱ ' + new Date(r.scheduled_for).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : (r.requested_at ? new Date(r.requested_at).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—')}</td>
                <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>{r.pickup_label} → {r.dropoff_label}</td>
                <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>{r.business_name || <span style={{ color: 'var(--accent-deep)' }}>unmatched</span>}</td>
                <td style={{ padding: '9px 10px' }}>{r.tier_name || '—'}</td>
                <td style={{ padding: '9px 10px' }} className="tabular">{r.party_size}</td>
                <td style={{ padding: '9px 10px' }} className="tabular">{money(r.fare_final ?? r.fare_estimate)}</td>
                <td style={{ padding: '9px 10px', color: '#0a6b49' }} className="tabular">{money(r.platform_fee)}</td>
                <td style={{ padding: '9px 10px' }}><span className="ll-pill" style={{ background: 'var(--surface-2)', color: 'var(--ink-soft)' }}>{r.status}</span></td>
                <td style={{ padding: '9px 10px' }}><span className="ll-pill" style={PAY[r.payment_status] || { background: 'var(--surface-2)', color: 'var(--ink-faint)' }}>{r.payment_status || '—'}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
function Stat({ label, value }) {
  return <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }} className="rounded-xl p-3.5"><div className="ll-title tabular" style={{ fontSize: 26 }}>{value}</div><div style={{ color: 'var(--ink-soft)', fontSize: 12 }}>{label}</div></div>;
}

function Analytics({ rides }) {
  const done = rides.filter((r) => r.status === 'completed');
  const by = {};
  for (const r of done) {
    const k = r.captain_id || 'unmatched';
    (by[k] ||= { name: r.business_name || 'Unmatched', rides: 0, gross: 0, cut: 0, payout: 0, tips: 0 });
    const b = by[k];
    b.rides++; b.gross += r.fare_final ?? r.fare_estimate ?? 0; b.cut += r.platform_fee || 0;
    b.payout += r.captain_payout || 0; b.tips += r.tip || 0;
  }
  const rows = Object.values(by).sort((a, b) => b.gross - a.gross);
  const T = rows.reduce((t, b) => ({ rides: t.rides + b.rides, gross: t.gross + b.gross, cut: t.cut + b.cut, payout: t.payout + b.payout, tips: t.tips + b.tips }), { rides: 0, gross: 0, cut: 0, payout: 0, tips: 0 });
  const tippedCount = done.filter((r) => (r.tip || 0) > 0).length;
  const avgFare = T.rides ? T.gross / T.rides : 0;
  const avgTip = tippedCount ? T.tips / tippedCount : 0;
  const maxGross = Math.max(1, ...rows.map((r) => r.gross));
  const th = { padding: '8px 10px', borderBottom: '1px solid var(--line)', fontWeight: 600, textAlign: 'left', color: 'var(--ink-soft)', fontSize: 11.5 };
  const num = { padding: '9px 10px', textAlign: 'right' };

  return (
    <div className="space-y-4">
      <Card title="Platform analytics">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat label="Completed rides" value={T.rides} />
          <Stat label="Gross volume" value={money(T.gross)} />
          <Stat label="Platform revenue" value={money(T.cut)} />
          <Stat label="Captain payouts" value={money(T.payout)} />
          <Stat label="Total tips" value={money(T.tips)} />
          <Stat label="Avg fare" value={money(avgFare)} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          <Stat label="Avg tip (when tipped)" value={money(avgTip)} />
          <Stat label="Tipped rides" value={`${tippedCount}/${T.rides}`} />
          <Stat label="Active drivers" value={rows.length} />
        </div>
      </Card>

      <Card title="By driver">
        {rows.length === 0 ? <div style={{ color: 'var(--ink-faint)' }} className="text-sm">No completed rides yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 680, borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Driver</th><th style={{ ...th, textAlign: 'right' }}>Rides</th><th style={{ ...th, textAlign: 'right' }}>Gross</th>
                <th style={{ ...th, textAlign: 'right' }}>Your cut</th><th style={{ ...th, textAlign: 'right' }}>Payout</th>
                <th style={{ ...th, textAlign: 'right' }}>Tips</th><th style={{ ...th, textAlign: 'right' }}>Avg fare</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {rows.map((b, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--line)' }}>
                    <td style={{ padding: '9px 10px', fontWeight: 600 }}>{b.name}</td>
                    <td style={num} className="tabular">{b.rides}</td>
                    <td style={num} className="tabular">{money(b.gross)}</td>
                    <td style={{ ...num, color: '#0a6b49' }} className="tabular">{money(b.cut)}</td>
                    <td style={num} className="tabular">{money(b.payout)}</td>
                    <td style={{ ...num, color: 'var(--good)' }} className="tabular">{money(b.tips)}</td>
                    <td style={num} className="tabular">{money(b.rides ? b.gross / b.rides : 0)}</td>
                    <td style={{ padding: '9px 10px', width: 120 }}><div style={{ height: 8, borderRadius: 4, background: 'var(--brand)', width: `${(b.gross / maxGross) * 100}%`, minWidth: 4 }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
